import { useMemo, useRef, useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { Button } from "@fawkes/ui";
import { Image, RotateCcw, Send, Sparkles, Trash2, Upload } from "lucide-react";

import { OntwerpRenderer, type Bril } from "./OntwerpRenderer";
import { voorbeeldAggregaten, voorbeeldrijenVoor } from "./voorbeelddata";
import type { Blok, Catalogus, Ontwerp, OntwerpSamenvatting, Rij, Versie } from "./ontwerpTypes";
import "./schermbouwer.css";

/**
 * De AI-schermbouwer.
 *
 * <p>Links het gesprek, rechts wat eruit komt. Die volgorde is niet toevallig: de gebruiker praat in
 * zijn eigen woorden en ziet meteen wat dat oplevert, zonder ooit een veldnaam of een conditie te
 * hoeven kennen.
 *
 * <p>Twee dingen zijn met opzet zichtbaar in plaats van weggemoffeld. <b>Wat de AI weglaat</b> krijgt
 * een eigen plek in het gesprek -- vroeg iemand om een belknop, dan hoort hij te weten dat die er niet
 * is in plaats van hem later te missen. En <b>wat de server afkeurt</b> staat er ook, want een ontwerp
 * dat stilletjes niet opslaat is erger dan een foutmelding.
 *
 * <p>Het voorbeeld staat standaard op voorbeelddata (besluit 11.1). Die data is expres rommelig: lege
 * velden, een veel te lange naam, verstreken en nog komende tijdstippen. Een ontwerp dat daar goed
 * uitziet, overleeft ook een echte dag.
 */

type Berichtsoort = "ik" | "ai" | "weggelaten" | "fout";

interface Bericht {
  soort: Berichtsoort;
  tekst: string;
}

interface AiAntwoord {
  gelukt: boolean;
  configuratie: Ontwerp | null;
  versie: number;
  melding: string;
  bevindingen: string[];
}

interface OpslagAntwoord {
  gelukt: boolean;
  versie: number;
  bevindingen: string[];
}

/** Hoe een onderdeel heet in de lijst en in een vergelijking: het veld, de actie, anders het blok. */
function naamVanBlok(blok: Blok): string {
  return blok.veld ?? blok.actie ?? blok.zorgschema ?? blok.blok;
}

export function Schermbouwer({ ontwerpId, schermtype }: { ontwerpId: string; schermtype: string }) {
  const t = useTranslate();
  const { mutateAsync } = useCustomMutation();

  const [gesprek, setGesprek] = useState<Bericht[]>([]);
  const [wens, setWens] = useState("");
  const [bezig, setBezig] = useState(false);
  const [ontwerp, setOntwerp] = useState<Ontwerp | null>(null);
  const [echteData, setEchteData] = useState(false);
  const [gekozen, setGekozen] = useState<number | null>(null);
  const [afbeelding, setAfbeelding] = useState<{ naam: string; base64: string; mediaType: string } | null>(null);
  // Bekijk als rol (fase 2). De vraag die een ontwerper echt heeft is niet "wat ziet rol X" maar
  // "wat ziet een collega die dit recht niet heeft" -- en dat is wat je hier kiest.
  const [zonderRecht, setZonderRecht] = useState<string>("");
  const [vergelijkMet, setVergelijkMet] = useState<number | null>(null);
  const [verschil, setVerschil] = useState<{ erbij: string[]; eraf: string[]; van: number } | null>(null);
  const bestandKiezer = useRef<HTMLInputElement>(null);

  const { data: catalogusData } = useCustom<Catalogus>({
    url: `schermbouwer/catalogus?schermtype=${encodeURIComponent(schermtype)}`,
    method: "get",
  });
  const catalogus = catalogusData?.data;

  const { data: rollenData } = useCustom<string[]>({
    url: `schermbouwer/rollen?schermtype=${encodeURIComponent(schermtype)}`,
    method: "get",
  });
  const rollen = rollenData?.data ?? [];

  // De catalogus door de ogen van zo een collega: alle rechten van dit tegeltype behalve het gekozene.
  // De server snijdt dat alsnog tegen je eigen rechten, dus je krijgt hoogstens minder te zien.
  const rolVraag = rollen
    .filter((r) => r !== zonderRecht)
    .map((r) => `&alsRol=${encodeURIComponent(r)}`)
    .join("");

  const { data: rolCatalogusData } = useCustom<Catalogus>({
    url: `schermbouwer/catalogus?schermtype=${encodeURIComponent(schermtype)}${rolVraag}`,
    method: "get",
    queryOptions: { enabled: zonderRecht !== "" },
  });

  const bril: Bril | undefined = useMemo(() => {
    if (zonderRecht === "") return undefined;
    const c = rolCatalogusData?.data;
    if (!c) return undefined;
    return { velden: new Set(c.velden.map((v) => v.id)), acties: new Set(c.acties.map((a) => a.id)) };
  }, [zonderRecht, rolCatalogusData]);

  const { data: logboekData, refetch: herlaadLogboek } = useCustom<
    { gebeurtenis: string; versie: number | null; toelichting: string | null; doorNaam: string | null; tijdstip: string }[]
  >({ url: `schermbouwer/ontwerpen/${ontwerpId}/logboek`, method: "get" });
  const logboek = logboekData?.data ?? [];

  const { data: ontwerpData, refetch: herlaadOntwerp } = useCustom<{
    ontwerp: OntwerpSamenvatting;
    configuratie: Ontwerp | null;
    versie: number;
  }>({ url: `schermbouwer/ontwerpen/${ontwerpId}`, method: "get" });

  const { data: versieData, refetch: herlaadVersies } = useCustom<Versie[]>({
    url: `schermbouwer/ontwerpen/${ontwerpId}/versies`,
    method: "get",
  });

  // De server is de bron; de lokale staat is er alleen om tussen twee verzoeken iets te kunnen tonen.
  const huidig = ontwerp ?? ontwerpData?.data?.configuratie ?? null;
  const samenvatting = ontwerpData?.data?.ontwerp;
  const versies = versieData?.data ?? [];

  const voorbeeldrijen = useMemo(() => voorbeeldrijenVoor(schermtype), [schermtype]);

  // De server levert de rijen en de tellingen samen: een badge met een getal dat het scherm zelf
  // uitrekent zou bij echte gegevens iets anders kunnen zeggen dan de tegel straks op het dashboard.
  const { data: echteGegevens } = useCustom<{ rijen: Rij[]; aggregaten: Record<string, number> }>({
    url: `schermbouwer/ontwerpen/${ontwerpId}/gegevens`,
    method: "get",
    queryOptions: { enabled: echteData },
  });

  const rijen: Rij[] = echteData ? echteGegevens?.data?.rijen ?? [] : voorbeeldrijen;
  const voorbeeldTellingen = useMemo(() => voorbeeldAggregaten(voorbeeldrijen), [voorbeeldrijen]);
  const aggregaten = echteData ? echteGegevens?.data?.aggregaten ?? {} : voorbeeldTellingen;

  function zeg(soort: Berichtsoort, tekst: string) {
    setGesprek((eerder) => [...eerder, { soort, tekst }]);
  }

  async function vraagAi() {
    const vraag = wens.trim();
    if (!vraag || bezig) return;
    setBezig(true);
    zeg("ik", vraag);
    setWens("");

    try {
      const antwoord = await mutateAsync({
        url: `schermbouwer/ontwerpen/${ontwerpId}/ai`,
        method: "post",
        values: {
          wens: vraag,
          afbeelding: afbeelding?.base64 ?? null,
          mediaType: afbeelding?.mediaType ?? null,
        },
        successNotification: false,
        errorNotification: false,
      });
      const uit = antwoord?.data as unknown as AiAntwoord;

      if (!uit?.gelukt) {
        zeg("fout", uit?.melding ?? t("schermbouwer.fout.algemeen"));
        if (uit?.bevindingen?.length) {
          zeg("fout", uit.bevindingen.join("\n"));
        }
        return;
      }
      setOntwerp(uit.configuratie);
      setAfbeelding(null);
      // Het weggelaten-deel krijgt een eigen bericht: dat moet opvallen, niet wegzakken.
      const heeftWeggelaten = (uit.configuratie?.weggelaten?.length ?? 0) > 0;
      zeg(heeftWeggelaten ? "weggelaten" : "ai", uit.melding);
      await Promise.all([herlaadOntwerp(), herlaadVersies(), herlaadLogboek()]);
    } catch {
      zeg("fout", t("schermbouwer.fout.algemeen"));
    } finally {
      setBezig(false);
    }
  }

  /** Direct bewerken gaat door dezelfde poort als de AI; alleen zonder AI-verbruik. */
  async function bewaar(nieuw: Ontwerp) {
    setOntwerp(nieuw);
    try {
      const antwoord = await mutateAsync({
        url: `schermbouwer/ontwerpen/${ontwerpId}/configuratie`,
        method: "put",
        values: nieuw as unknown as Record<string, unknown>,
        successNotification: false,
        errorNotification: false,
      });
      const uit = antwoord?.data as unknown as OpslagAntwoord;
      if (!uit?.gelukt) {
        zeg("fout", t("schermbouwer.fout.nietOpgeslagen"));
        if (uit?.bevindingen?.length) zeg("fout", uit.bevindingen.join("\n"));
        await herlaadOntwerp();
        setOntwerp(null);
        return;
      }
      await Promise.all([herlaadOntwerp(), herlaadVersies(), herlaadLogboek()]);
    } catch {
      zeg("fout", t("schermbouwer.fout.nietOpgeslagen"));
    }
  }

  function onderdelenVan(o: Ontwerp): Blok[] {
    return o.regel?.onderdelen ?? [];
  }

  /**
   * Het kleinste ontwerp dat de validator goedkeurt.
   *
   * <p>Nodig omdat niet iedereen met de AI wil beginnen. Wie gewoon drie velden naast elkaar wil, moet
   * dat kunnen door ze aan te klikken -- een scherm dat eerst een gesprek eist voordat je iets mag
   * slepen, dwingt AI-gebruik af waar het plan juist keuze belooft.
   */
  function leegOntwerp(): Ontwerp {
    return {
      schemaVersie: 1,
      schermtype,
      titel: samenvatting?.naam ?? t("schermbouwer.nieuweTegel"),
      bron: { entiteit: catalogus?.bronnen?.[0] ?? "", maxRegels: 10 },
      indeling: "compact",
      regel: { blok: "lijstregel", onderdelen: [] },
    };
  }

  function metOnderdelen(o: Ontwerp, onderdelen: Blok[]): Ontwerp {
    return { ...o, regel: { ...o.regel, onderdelen } };
  }

  function verwijder(index: number) {
    if (!huidig) return;
    const over = onderdelenVan(huidig).filter((_, i) => i !== index);
    setGekozen(null);
    void bewaar(metOnderdelen(huidig, over));
  }

  function verplaats(van: number, naar: number) {
    if (!huidig || van === naar) return;
    const lijst = [...onderdelenVan(huidig)];
    const [eruit] = lijst.splice(van, 1);
    lijst.splice(naar, 0, eruit);
    setGekozen(naar);
    void bewaar(metOnderdelen(huidig, lijst));
  }

  function voegVeldToe(veldId: string, type: string) {
    const basis = huidig ?? leegOntwerp();
    const blok: Blok =
      type === "afbeelding"
        ? { blok: "afbeelding", veld: veldId, grootte: "s" }
        : { blok: "tekst", veld: veldId };
    void bewaar(metOnderdelen(basis, [...onderdelenVan(basis), blok]));
  }

  function voegActieToe(actieId: string) {
    const basis = huidig ?? leegOntwerp();
    void bewaar(metOnderdelen(basis, [...onderdelenVan(basis), { blok: "knop", actie: actieId }]));
  }

  /**
   * Wat er tussen twee versies veranderd is.
   *
   * <p>Niet als tekstverschil van de JSON -- dat leest niemand en het staat vol met haakjes die niets
   * betekenen. Wat een ontwerper wil weten is welke onderdelen erbij kwamen en welke weggingen, en dat
   * is precies wat hij in de tegel terugziet.
   */
  function vergelijk(versie: number) {
    if (!samenvatting) return;
    setVergelijkMet(versie);

    // Geen verzoek nodig: de versielijst draagt de configuraties al met zich mee. Dat scheelt niet
    // alleen een rondje, het maakt vergelijken ook meteen zichtbaar in plaats van na een wachtje.
    const oude = versies.find((v) => v.versie === versie);
    const nieuwe = versies.find((v) => v.versie === samenvatting.huidigeVersie);
    if (!oude || !nieuwe) {
      setVerschil(null);
      return;
    }
    const lijstVan = (ruw: string): string[] => {
      try {
        return ((JSON.parse(ruw) as Ontwerp).regel?.onderdelen ?? []).map(naamVanBlok);
      } catch {
        return [];
      }
    };
    const vanLijst = lijstVan(oude.configuratie);
    const totLijst = lijstVan(nieuwe.configuratie);
    setVerschil({
      van: versie,
      erbij: totLijst.filter((n) => !vanLijst.includes(n)),
      eraf: vanLijst.filter((n) => !totLijst.includes(n)),
    });
  }

  async function zetTerug(versie: number) {
    await mutateAsync({
      url: `schermbouwer/ontwerpen/${ontwerpId}/versies/${versie}/terugzetten`,
      method: "post",
      values: {},
      successNotification: false,
      errorNotification: false,
    });
    setOntwerp(null);
    await Promise.all([herlaadOntwerp(), herlaadVersies(), herlaadLogboek()]);
    zeg("ai", t("schermbouwer.teruggezet", { args: [versie] }));
  }

  /**
   * Vragen of de tegel op een gedeeld dashboard mag.
   *
   * <p>Apart van Publiceren, want het is iets anders: publiceren raakt je eigen scherm, delen raakt
   * dat van collega's. Een knop die stilletjes het tweede doet als je het eerste bedoelde, is precies
   * hoe er ongevraagd tegels op andermans dashboard belanden.
   */
  async function vraagDelenAan() {
    try {
      await mutateAsync({
        url: `schermbouwer/ontwerpen/${ontwerpId}/delen-aanvragen`,
        method: "post",
        values: { reden: null },
        successNotification: false,
        errorNotification: false,
      });
      zeg("ai", t("schermbouwer.delenAangevraagd"));
      await herlaadLogboek();
    } catch {
      zeg("fout", t("schermbouwer.delenMislukt"));
    }
  }

  async function publiceer() {
    await mutateAsync({
      url: `schermbouwer/ontwerpen/${ontwerpId}/publiceren`,
      method: "post",
      values: {},
      successNotification: false,
      errorNotification: false,
    });
    await herlaadOntwerp();
    zeg("ai", t("schermbouwer.gepubliceerd"));
  }

  function kiesAfbeelding(bestand: File | undefined) {
    if (!bestand) return;
    const lezer = new FileReader();
    lezer.onload = () => {
      const uit = String(lezer.result);
      const komma = uit.indexOf(",");
      setAfbeelding({
        naam: bestand.name,
        base64: komma >= 0 ? uit.slice(komma + 1) : uit,
        mediaType: bestand.type || "image/png",
      });
    };
    lezer.readAsDataURL(bestand);
  }

  return (
    <div className="sb">
      {/* ---- links: het gesprek ---- */}
      <div className="sb__kolom">
        <div className="sb__kaart">
          <span className="sb__kaarttitel">
            <Sparkles size={16} aria-hidden="true" />
            {t("schermbouwer.gesprek")}
          </span>

          <div className="sb__gesprek">
            {gesprek.length === 0 && (
              <p className="sb__waarschuwing">{t("schermbouwer.beginUitleg")}</p>
            )}
            {gesprek.map((b, i) => (
              <div key={i} className={`sb__bericht sb__bericht--${b.soort}`}>
                {b.tekst}
              </div>
            ))}
          </div>

          <div className="sb__invoer">
            <textarea
              value={wens}
              placeholder={t("schermbouwer.plaatshouder")}
              aria-label={t("schermbouwer.gesprek")}
              onChange={(e) => setWens(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) void vraagAi();
              }}
            />
            <div className="sb__invoerbalk">
              <Button variant="primary" disabled={bezig || !wens.trim()} onClick={() => void vraagAi()}>
                <Send size={15} aria-hidden="true" />
                {t(bezig ? "schermbouwer.bezig" : "schermbouwer.versturen")}
              </Button>
              <Button variant="secondary" onClick={() => bestandKiezer.current?.click()}>
                <Upload size={15} aria-hidden="true" />
                {t("schermbouwer.afbeelding")}
              </Button>
              <input
                ref={bestandKiezer}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => kiesAfbeelding(e.target.files?.[0])}
              />
              {afbeelding && (
                <span className="sb__bijlage">
                  <Image size={14} aria-hidden="true" />
                  {afbeelding.naam}
                  <button type="button" className="sb-knop" onClick={() => setAfbeelding(null)}>
                    {t("schermbouwer.weghalen")}
                  </button>
                </span>
              )}
            </div>
            {/* Harde regel 8 plus 6.3: de gebruiker hoort te weten wat er met zijn afbeelding gebeurt. */}
            <p className="sb__waarschuwing">{t("schermbouwer.afbeeldingUitleg")}</p>
          </div>
        </div>

        <div className="sb__kaart">
          <span className="sb__kaarttitel">{t("schermbouwer.versies")}</span>
          {versies.length === 0 ? (
            <p className="sb__waarschuwing">{t("schermbouwer.geenVersies")}</p>
          ) : (
            <ul className="sb__versies">
              {versies.map((v) => (
                <li key={v.versie} className="sb__versie">
                  <span className="sb__versie__nr">{v.versie}</span>
                  <span className="sb__versie__wat">
                    {v.toelichting || (v.doorAi ? t("schermbouwer.doorAi") : t("schermbouwer.metDeHand"))}
                  </span>
                  <button
                    type="button"
                    className="sb-knop"
                    disabled={v.versie === samenvatting?.huidigeVersie}
                    aria-pressed={vergelijkMet === v.versie}
                    onClick={() => vergelijk(v.versie)}
                  >
                    {t("schermbouwer.vergelijken")}
                  </button>
                  <button
                    type="button"
                    className="sb-knop"
                    disabled={v.versie === samenvatting?.huidigeVersie}
                    onClick={() => void zetTerug(v.versie)}
                  >
                    <RotateCcw size={13} aria-hidden="true" />
                    {t("schermbouwer.terugzetten")}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {verschil && (
            <p className="sb__waarschuwing" role="status">
              {t("schermbouwer.verschilMet", { args: [verschil.van, samenvatting?.huidigeVersie ?? 0] })}{" "}
              {verschil.erbij.length > 0 && t("schermbouwer.erbij", { args: [verschil.erbij.join(", ")] })}{" "}
              {verschil.eraf.length > 0 && t("schermbouwer.eraf", { args: [verschil.eraf.join(", ")] })}
              {verschil.erbij.length === 0 && verschil.eraf.length === 0 && t("schermbouwer.geenVerschil")}
            </p>
          )}
        </div>
      </div>

      {/* ---- rechts: het voorbeeld ---- */}
      <div className="sb__kolom">
        <div className="sb__kaart">
          <div className="sb__voorbeeldbalk">
            <span className="sb__kaarttitel">{t("schermbouwer.voorbeeld")}</span>
            <label className="sb__schakelaar">
              <input type="checkbox" checked={echteData} onChange={(e) => setEchteData(e.target.checked)} />
              {t("schermbouwer.echteData")}
            </label>
            {rollen.length > 0 && (
              <label className="sb__schakelaar">
                {t("schermbouwer.bekijkAls")}
                <select value={zonderRecht} onChange={(e) => setZonderRecht(e.target.value)}>
                  <option value="">{t("schermbouwer.alsMijzelf")}</option>
                  {rollen.map((r) => (
                    <option key={r} value={r}>
                      {t("schermbouwer.zonder", { args: [r] })}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {samenvatting && (
              <span className="sb__waarschuwing">
                {t(`schermbouwer.status.${samenvatting.status}`)} · v{samenvatting.huidigeVersie}
              </span>
            )}
          </div>

          {huidig ? (
            <OntwerpRenderer ontwerp={huidig} rijen={rijen} aggregaten={aggregaten} bril={bril} alleenTonen />
          ) : (
            <p className="sb__waarschuwing">{t("schermbouwer.nogGeenOntwerp")}</p>
          )}
        </div>

        {huidig && (
          <div className="sb__kaart">
            <span className="sb__kaarttitel">{t("schermbouwer.onderdelenInDeTegel")}</span>
            <div className="sb__onderdelen">
              {onderdelenVan(huidig).map((o, i) => (
                <button
                  key={i}
                  type="button"
                  className="sb__onderdeel"
                  draggable
                  aria-pressed={gekozen === i}
                  onDragStart={() => setGekozen(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => gekozen !== null && verplaats(gekozen, i)}
                  onClick={() => setGekozen(gekozen === i ? null : i)}
                >
                  {o.veld ?? o.actie ?? o.zorgschema ?? o.blok}
                </button>
              ))}
            </div>
            {gekozen !== null && (
              <div className="sb__voet">
                <Button variant="secondary" onClick={() => verplaats(gekozen, Math.max(0, gekozen - 1))}>
                  {t("schermbouwer.naarLinks")}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => verplaats(gekozen, Math.min(onderdelenVan(huidig).length - 1, gekozen + 1))}
                >
                  {t("schermbouwer.naarRechts")}
                </Button>
                <Button variant="secondary" onClick={() => verwijder(gekozen)}>
                  <Trash2 size={15} aria-hidden="true" />
                  {t("schermbouwer.verwijderen")}
                </Button>
              </div>
            )}
          </div>
        )}

        {catalogus && (
          <div className="sb__kaart">
            <span className="sb__kaarttitel">{t("schermbouwer.toevoegen")}</span>
            <div className="sb__onderdelen">
              <span className="sb__onderdeelgroep">{t("schermbouwer.velden")}</span>
              {catalogus.velden.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  className="sb__onderdeel"
                  onClick={() => voegVeldToe(v.id, v.type)}
                >
                  + {v.id}
                </button>
              ))}
              <span className="sb__onderdeelgroep">{t("schermbouwer.acties")}</span>
              {catalogus.acties.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className="sb__onderdeel"
                  onClick={() => voegActieToe(a.id)}
                >
                  + {a.id}
                </button>
              ))}
            </div>
            {/* Wat hier niet staat, bestaat niet voor deze tegel. Dat hardop zeggen scheelt zoeken. */}
            <p className="sb__waarschuwing">{t("schermbouwer.catalogusUitleg")}</p>
          </div>
        )}

        <div className="sb__voet">
          <Button variant="primary" disabled={!huidig} onClick={() => void publiceer()}>
            {t("schermbouwer.publiceren")}
          </Button>
          <Button variant="secondary" disabled={!huidig} onClick={() => void vraagDelenAan()}>
            {t("schermbouwer.delenAanvragen")}
          </Button>
        </div>

        {logboek.length > 0 && (
          <div className="sb__kaart">
            <span className="sb__kaarttitel">{t("schermbouwer.logboek")}</span>
            <ul className="sb__versies">
              {logboek.slice(0, 12).map((r, i) => (
                <li key={i} className="sb__versie">
                  <span className="sb__versie__nr">{r.versie ?? ""}</span>
                  <span className="sb__versie__wat">
                    {t(`schermbouwer.gebeurtenis.${r.gebeurtenis}`)}
                    {r.doorNaam ? ` — ${r.doorNaam}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
